import { useCallback, useEffect, useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import {
  canWrite,
  errorMessage,
  financeGet,
  financeSend,
  num,
  todayValue,
  won,
  type FinanceCtx,
  type FinanceSettings,
} from '../financeApi';
import PrintPortal from '../common/PrintPortal';
import ReceiptDocument, { type ReceiptDonor } from './ReceiptDocument';

type Donor = ReceiptDonor & {
  phone: string;
  count: number;
  byCategory: Record<string, number>;
  issue: { serialNo: string; totalAmount: number; issuedAt: string; issueCount: number } | null;
};

type ReceiptData = {
  year: number;
  donors: Donor[];
  unlinked: { donorName: string; total: number; count: number }[];
};

// 기부금 영수증(연말정산): 성도별 연간 헌금 합산 → 일련번호 부여·발급 기록 → 영수증 인쇄/PDF, 국세청 제출 준비용 엑셀.
// 합산 대상: 교적과 연결된 헌금 중 무명이 아니고, 확인대기가 아니며, 재정 설정에서 '영수증 대상'인 항목.
// 주민등록번호는 서버에 저장하지 않고 인쇄할 때만 입력받는다.
export default function DonationReceipts({ ctx }: { ctx: FinanceCtx }) {
  const thisYear = new Date().getFullYear();
  // 연말정산은 보통 다음 해 1~2월에 하므로, 1~3월에는 지난 해를 기본으로 연다.
  const [year, setYear] = useState(new Date().getMonth() < 3 ? thisYear - 1 : thisYear);
  const [data, setData] = useState<ReceiptData | null>(null);
  const [settings, setSettings] = useState<FinanceSettings | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [rrnStep, setRrnStep] = useState<Donor[] | null>(null);
  const [rrns, setRrns] = useState<Record<number, string>>({});
  const [printing, setPrinting] = useState<{ donors: Donor[]; serials: Record<number, string> } | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setError(null);
    setData(null);
    setSelected(new Set());
    financeGet<ReceiptData>(ctx, '/reports/receipts', { year })
      .then(setData)
      .catch((err) => setError(errorMessage(err, '기부금 합산을 불러오지 못했습니다.')));
  }, [ctx, year]);

  useEffect(load, [load]);
  useEffect(() => {
    financeGet<FinanceSettings>(ctx, '/settings').then(setSettings).catch(() => setSettings(null));
  }, [ctx]);

  const donors = useMemo(
    () => (data?.donors ?? []).filter((d) => !q.trim() || d.name.includes(q.trim()) || d.phone.includes(q.trim())),
    [data, q],
  );
  const totalAll = data?.donors.reduce((s, d) => s + d.total, 0) ?? 0;
  const issuedCount = data?.donors.filter((d) => d.issue).length ?? 0;
  const changedCount = data?.donors.filter((d) => d.issue && d.issue.totalAmount !== d.total).length ?? 0;
  const missingChurchInfo = settings
    ? !settings.churchName || !settings.registrationNo || !settings.address || !settings.representative
    : false;

  const toggle = (id: number) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  });
  const allSelected = donors.length > 0 && donors.every((d) => selected.has(d.memberId));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(donors.map((d) => d.memberId)));
  const chosen = (data?.donors ?? []).filter((d) => selected.has(d.memberId));

  const startIssue = () => {
    if (chosen.length === 0) return;
    setRrnStep(chosen);
  };

  const issueAndPrint = async () => {
    if (!rrnStep) return;
    setBusy(true);
    try {
      let serials: Record<number, string> = Object.fromEntries(
        rrnStep.filter((d) => d.issue).map((d) => [d.memberId, d.issue!.serialNo]),
      );
      if (canWrite(ctx)) {
        const res = await financeSend<{ data: { memberId: number; serialNo: string }[] }>(ctx, 'post', '/reports/receipts/issue', {
          year,
          items: rrnStep.map((d) => ({ memberId: d.memberId, totalAmount: d.total })),
        });
        serials = Object.fromEntries(res.data.map((i) => [i.memberId, i.serialNo]));
      }
      setPrinting({ donors: rrnStep, serials });
      setRrnStep(null);
    } catch (err) {
      window.alert(errorMessage(err, '발급 기록을 남기지 못했습니다.'));
    } finally {
      setBusy(false);
    }
  };

  const exportExcel = () => {
    const rows = (chosen.length > 0 ? chosen : data?.donors ?? []);
    const aoa: (string | number)[][] = [
      ['일련번호', '기부자 성명', '주민등록번호', '주소', '연락처', '기부유형', '유형코드', '구분', '기부년도', '기부금액', '발급일'],
      ...rows.map((d) => [
        d.issue?.serialNo ?? '',
        d.name,
        '',
        d.address,
        d.phone,
        '종교단체',
        41,
        '금전',
        year,
        d.total,
        d.issue?.issuedAt?.slice(0, 10) ?? '',
      ]),
    ];
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!cols'] = [{ wch: 12 }, { wch: 10 }, { wch: 16 }, { wch: 30 }, { wch: 14 }, { wch: 10 }, { wch: 8 }, { wch: 6 }, { wch: 8 }, { wch: 12 }, { wch: 12 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, `${year}년 기부금`);
    XLSX.writeFile(wb, `${year}년_기부금_제출자료.xlsx`);
  };

  return (
    <div className="manage-panel">
      <div className="manage-panel-header">
        <div>
          <h2 className="manage-panel-title">기부금 영수증</h2>
          <p className="manage-panel-desc">성도별 연간 헌금을 합산해 기부금 영수증을 발급·출력하고, 국세청 연말정산 자료 제출에 쓸 엑셀을 내려받습니다.</p>
        </div>
        <select className="fin-select" value={year} onChange={(e) => setYear(Number(e.target.value))}>
          {Array.from({ length: 6 }, (_, i) => thisYear - i).map((y) => <option key={y} value={y}>{y}년 귀속</option>)}
        </select>
      </div>

      {missingChurchInfo && (
        <div className="fin-inline-warn">
          영수증에 들어갈 교회 정보(단체명·고유번호·소재지·대표자)가 비어 있습니다. '재정 설정 &gt; 교회 정보'에서 먼저 입력해 주세요.
        </div>
      )}
      {error && <div className="manage-state manage-state--error">{error}</div>}
      {!error && !data && <div className="manage-state">불러오는 중입니다...</div>}

      {data && (
        <>
          <div className="fin-kpis fin-kpis--compact">
            <div className="fin-kpi"><span>영수증 대상 성도</span><strong>{data.donors.length}명</strong></div>
            <div className="fin-kpi"><span>합산 금액</span><strong>{won(totalAll)}</strong></div>
            <div className="fin-kpi"><span>발급 완료</span><strong>{issuedCount}명</strong>
              {changedCount > 0 && <span className="fin-delta is-up">발급 후 금액 변동 {changedCount}명</span>}</div>
            <div className="fin-kpi"><span>교적 미연결 헌금</span><strong>{data.unlinked.length}명</strong><span className="fin-delta">합산 제외</span></div>
          </div>

          {data.unlinked.length > 0 && (
            <details className="fin-inline-warn">
              <summary>
                교적과 연결되지 않은 이름으로 기록된 헌금 {data.unlinked.length}명 ({won(data.unlinked.reduce((s, u) => s + u.total, 0))})은
                영수증에 합산되지 않습니다. '헌금 등록·내역'에서 해당 내역을 수정해 성도를 연결해 주세요.
              </summary>
              <p>{data.unlinked.map((u) => `${u.donorName}(${num(u.total)}원)`).join(', ')}</p>
            </details>
          )}

          <div className="manage-filterbar">
            <div className="manage-filterbar-search">
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="이름·연락처 검색" />
            </div>
            <span className="fin-muted">{selected.size}명 선택</span>
            <span className="fin-spacer" />
            <button type="button" className="manage-btn manage-btn--sm" onClick={exportExcel} disabled={data.donors.length === 0}>
              국세청 제출용 엑셀{selected.size > 0 ? ' (선택)' : ''}
            </button>
            <button type="button" className="manage-btn manage-btn--sm manage-btn--primary" onClick={startIssue} disabled={selected.size === 0}>
              {canWrite(ctx) ? '선택 영수증 발급·인쇄' : '선택 영수증 인쇄'}
            </button>
          </div>

          <div className="manage-card-block">
            <div className="manage-table-wrap">
              <table className="manage-table">
                <thead>
                  <tr>
                    <th style={{ width: 36 }}><input type="checkbox" checked={allSelected} onChange={toggleAll} /></th>
                    <th>성도</th>
                    <th>연락처</th>
                    <th>헌금 항목</th>
                    <th className="fin-num" style={{ width: 70 }}>건수</th>
                    <th className="fin-num" style={{ width: 130 }}>연간 합계</th>
                    <th style={{ width: 170 }}>발급</th>
                  </tr>
                </thead>
                <tbody>
                  {donors.length === 0 && <tr><td colSpan={7} className="manage-table-empty">영수증 대상 헌금이 없습니다.</td></tr>}
                  {donors.map((d) => (
                    <tr key={d.memberId}>
                      <td><input type="checkbox" checked={selected.has(d.memberId)} onChange={() => toggle(d.memberId)} /></td>
                      <td><b>{d.name}</b></td>
                      <td className="manage-table-meta">{d.phone}</td>
                      <td className="manage-table-meta">
                        {Object.entries(d.byCategory).sort((a, b) => b[1] - a[1]).map(([c, v]) => `${c} ${num(v)}`).join(' · ')}
                      </td>
                      <td className="fin-num">{d.count}</td>
                      <td className="fin-num"><b>{num(d.total)}</b></td>
                      <td>
                        {d.issue ? (
                          <>
                            <span className="manage-badge manage-badge--green">{d.issue.serialNo}</span>
                            <div className="manage-table-meta">
                              {d.issue.issuedAt.slice(0, 10)}{d.issue.issueCount > 1 ? ` · ${d.issue.issueCount}회` : ''}
                              {d.issue.totalAmount !== d.total && <span className="fin-tone is-warn"> 금액 변동</span>}
                            </div>
                          </>
                        ) : (
                          <span className="fin-muted">미발급</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <p className="fin-muted">
            엑셀은 국세청 연말정산 간소화 자료 제출(홈택스 기부금 영수증 발급명세 제출)을 준비하기 위한 정리 자료입니다.
            주민등록번호는 보관하지 않으므로 제출 전에 직접 채워 주세요.
          </p>
        </>
      )}

      {rrnStep && (
        <div className="manage-modal-overlay" onClick={() => setRrnStep(null)}>
          <div className="manage-modal" onClick={(e) => e.stopPropagation()}>
            <div className="manage-modal-header">
              <h3>영수증 {rrnStep.length}장 {canWrite(ctx) ? '발급' : '인쇄'}</h3>
              <button type="button" className="manage-modal-close" onClick={() => setRrnStep(null)}>×</button>
            </div>
            <div className="manage-modal-body">
              <p className="manage-modal-desc">
                주민등록번호를 입력하면 영수증에 인쇄됩니다(선택). 입력한 번호는 저장되지 않습니다.
                {canWrite(ctx) && ' 발급하면 일련번호가 부여되고 발급 기록이 남습니다(재발급 시 같은 번호 유지).'}
              </p>
              <div className="fin-rrn-list">
                {rrnStep.map((d) => (
                  <label key={d.memberId} className="fin-rrn-row">
                    <span><b>{d.name}</b> <small>{won(d.total)}</small></span>
                    <input
                      value={rrns[d.memberId] ?? ''}
                      placeholder="000000-0000000"
                      maxLength={14}
                      onChange={(e) => setRrns({ ...rrns, [d.memberId]: e.target.value.replace(/[^\d-]/g, '') })}
                    />
                  </label>
                ))}
              </div>
            </div>
            <div className="manage-modal-footer">
              <button type="button" className="manage-btn" onClick={() => setRrnStep(null)}>취소</button>
              <button type="button" className="manage-btn manage-btn--primary" onClick={issueAndPrint} disabled={busy}>
                {busy ? '처리 중...' : canWrite(ctx) ? '발급하고 인쇄' : '인쇄'}
              </button>
            </div>
          </div>
        </div>
      )}

      {printing && settings && (
        <PrintPortal
          title={`${year}년_기부금영수증`}
          onDone={() => {
            setPrinting(null);
            setRrns({});
            load();
          }}
        >
          {printing.donors.map((d) => (
            <ReceiptDocument
              key={d.memberId}
              year={year}
              donor={d}
              rrn={rrns[d.memberId] ?? ''}
              serialNo={printing.serials[d.memberId] ?? ''}
              church={settings}
              issuedDate={todayValue()}
            />
          ))}
        </PrintPortal>
      )}
    </div>
  );
}
